import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { verifyPasswordResetCode } from "firebase/auth";
import { getPasswordRules, isPasswordValid } from "../utils/passwordValidation";
import { resetPasswordWithCode } from "../services/auth";
import { auth } from "../firebase/firebase";
import "./AuthPage.css";

const PUBLIC_URL = process.env.PUBLIC_URL || "";

type ResetStep = "checking" | "form" | "success" | "invalid" | "expired";

const ResetPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const oobCode = searchParams.get("oobCode") || searchParams.get("oob_code") || "";

  const [step, setStep] = useState<ResetStep>(oobCode ? "checking" : "invalid");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordFieldFocused, setPasswordFieldFocused] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [confirmError, setConfirmError] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const passwordRules = getPasswordRules(password);
  const showPasswordRules = passwordFieldFocused || password.length > 0;

  // Validate the oobCode on load so used/expired links show the correct screen immediately.
  useEffect(() => {
    if (!oobCode) {
      setStep("invalid");
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        await verifyPasswordResetCode(auth, oobCode);
        if (!cancelled) {
          setStep("form");
        }
      } catch (err: unknown) {
        if (cancelled) return;
        const code = err && typeof err === "object" && "code" in err ? (err as { code: string }).code : "";
        if (code === "auth/expired-action-code") {
          setStep("expired");
        } else {
          setStep("invalid");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [oobCode]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError("");
    setConfirmError("");
    setSubmitError("");
    if (!isPasswordValid(passwordRules)) {
      setPasswordError("Password does not meet all requirements.");
      return;
    }
    if (password !== confirmPassword) {
      setConfirmError("Passwords do not match.");
      return;
    }
    setSubmitting(true);
    try {
      await resetPasswordWithCode(oobCode, password);
      setStep("success");
    } catch (err: unknown) {
      const code = err && typeof err === "object" && "code" in err ? (err as { code: string }).code : "";
      if (code === "auth/expired-action-code") {
        setStep("expired");
      } else if (code === "auth/invalid-action-code") {
        setStep("invalid");
      } else {
        setSubmitError("Something went wrong. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const logoUrl = PUBLIC_URL ? `${PUBLIC_URL}/logo.svg` : "/logo.svg";

  if (step === "checking") {
    return (
      <div className="auth-page">
        <div className="auth-card auth-card--forgot auth-card--loading">
          <img src={logoUrl} alt="Logo" className="auth-logo" />
          <div className="auth-loading-spinner" aria-label="Checking link" />
        </div>
      </div>
    );
  }

  if (!oobCode && step === "invalid") {
    return (
      <div className="auth-page">
        <div className="auth-card auth-card--forgot">
          <img src={logoUrl} alt="Logo" className="auth-logo" />
          <h1 className="auth-title">Invalid link</h1>
          <p className="auth-subtitle auth-subtitle--forgot">
            This password reset link is invalid or has already been used.
          </p>
          <Link to="/forgot-password" className="auth-btn" style={{ display: "inline-block", textAlign: "center", textDecoration: "none", marginTop: 16 }}>
            Request a new link
          </Link>
          <p className="auth-bottom auth-bottom--forgot">
            Back to <Link to="/login" className="auth-link">Log in</Link>
          </p>
        </div>
      </div>
    );
  }

  if (step === "success") {
    return (
      <div className="auth-page">
        <div className="auth-card auth-card--forgot">
          <img src={logoUrl} alt="Logo" className="auth-logo" />
          <h1 className="auth-title">Password changed</h1>
          <p className="auth-subtitle auth-subtitle--forgot">
            Your password has been successfully changed.
          </p>
          <p className="auth-subtitle auth-subtitle--forgot--text">
            Please log in to continue.
          </p>
          <button type="button" className="auth-btn" onClick={() => navigate("/login")}>
            Log in
          </button>
          <p className="auth-bottom auth-bottom--forgot">
            Back to <Link to="/login" className="auth-link">Log in</Link>
          </p>
        </div>
      </div>
    );
  }

  if (step === "invalid") {
    return (
      <div className="auth-page">
        <div className="auth-card auth-card--forgot">
          <img src={logoUrl} alt="Logo" className="auth-logo" />
          <h1 className="auth-title">Invalid verification link</h1>
          <p className="auth-subtitle auth-subtitle--forgot">
            This link has already been used or is invalid.
          </p>
          <Link to="/forgot-password" className="auth-btn" style={{ display: "inline-block", textAlign: "center", textDecoration: "none", marginTop: 16 }}>
            Request a new link
          </Link>
          <p className="auth-bottom auth-bottom--forgot">
            Back to <Link to="/login" className="auth-link">Log in</Link>
          </p>
        </div>
      </div>
    );
  }

  if (step === "expired") {
    return (
      <div className="auth-page">
        <div className="auth-card auth-card--forgot">
          <img src={logoUrl} alt="Logo" className="auth-logo" />
          <h1 className="auth-title">Expired link</h1>
          <p className="auth-subtitle auth-subtitle--forgot">
            This password reset link has expired. Links are valid for 24 hours.
          </p>
          <Link to="/forgot-password" className="auth-btn" style={{ display: "inline-block", textAlign: "center", textDecoration: "none", marginTop: 16 }}>
            Request a new link
          </Link>
          <p className="auth-bottom auth-bottom--forgot">
            Back to <Link to="/login" className="auth-link">Log in</Link>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-card auth-card--forgot">
        <img src={logoUrl} alt="Logo" className="auth-logo" />
        <h1 className="auth-title">Reset password</h1>
        <p className="auth-subtitle auth-subtitle--forgot">
          Enter your new password below.
        </p>
        <form onSubmit={handleSubmit}>
          <div className="auth-field">
            <label htmlFor="reset-password">New password</label>
            <div className={`auth-input-wrapper auth-input-wrapper--has-suffix${passwordError ? " auth-input-wrapper--error" : ""}`}>
              <input
                id="reset-password"
                type={showPassword ? "text" : "password"}
                className="auth-input"
                placeholder="Create a strong password"
                value={password}
                onChange={(e) => { setPassword(e.target.value); setPasswordError(""); }}
                onFocus={() => setPasswordFieldFocused(true)}
                onBlur={() => setPasswordFieldFocused(false)}
              />
              <span className="auth-input-suffix">
                <button
                  type="button"
                  className="auth-input-icon-btn"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  <img
                    src={`${PUBLIC_URL}/${showPassword ? "input-suffix.svg" : "Vector.svg"}`}
                    alt=""
                  />
                </button>
              </span>
            </div>
            {showPasswordRules && (
              <div className="auth-password-rules" aria-live="polite">
                {(
                  [
                    ["lowercase", "Lowercase"],
                    ["uppercase", "Uppercase"],
                    ["number", "Number"],
                    ["symbol", "Symbol"],
                    ["minLength", "10 character minimum"],
                  ] as const
                ).map(([key, label]) => {
                  const met = passwordRules[key];
                  return (
                    <span
                      key={key}
                      className={`auth-password-rule auth-password-rule--${met ? "met" : "unmet"}`}
                    >
                      <span className="auth-password-rule-icon">{met ? "✓" : "✗"}</span>
                      {label}
                    </span>
                  );
                })}
              </div>
            )}
            {passwordError && <p className="auth-field-error" role="alert">{passwordError}</p>}
          </div>
          <div className="auth-field">
            <label htmlFor="reset-confirm">Confirm password</label>
            <div className={`auth-input-wrapper auth-input-wrapper--has-suffix${confirmError ? " auth-input-wrapper--error" : ""}`}>
              <input
                id="reset-confirm"
                type={showConfirmPassword ? "text" : "password"}
                className="auth-input"
                placeholder="Re-enter your password"
                value={confirmPassword}
                onChange={(e) => { setConfirmPassword(e.target.value); setConfirmError(""); }}
              />
              <span className="auth-input-suffix">
                <button
                  type="button"
                  className="auth-input-icon-btn"
                  onClick={() => setShowConfirmPassword((v) => !v)}
                  aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                >
                  <img
                    src={`${PUBLIC_URL}/${showConfirmPassword ? "input-suffix.svg" : "Vector.svg"}`}
                    alt=""
                  />
                </button>
              </span>
            </div>
            {confirmError && <p className="auth-field-error" role="alert">{confirmError}</p>}
          </div>
          {submitError && <p className="auth-field-error" role="alert">{submitError}</p>}
          <button type="submit" className="auth-btn" disabled={submitting}>
            {submitting ? "Resetting…" : "Reset password"}
          </button>
        </form>
        <p className="auth-bottom auth-bottom--forgot">
          Back to <Link to="/login" className="auth-link">Log in</Link>
        </p>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
